"use client";

import { useCallback, useEffect, useState } from "react";
import {
  BookOpen,
  CalendarRange,
  CheckCircle2,
  History,
  Loader2,
  Target,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ExamSession, ProblemAttempt, ProblemHistory } from "@/lib/types";
import { UserBadge, BADGES, RARITY_STYLES } from "@/types/badge";
import { StaticBadgeMedal } from "@/components/BadgeComponents";
import PastExamCompletionCard from "@/components/user-button-pages/PastExamCompletionCard";
import { listExamSessions } from "@/lib/server-actions/examSessionActions";
import {
  getAllProblemHistoriesForSession,
  getProblemHistoryForSession,
} from "@/lib/server-actions/problem/history";
import { getUserBadgesForSession } from "@/lib/server-actions/badgeActions";
import ErrorNoteStatsView from "@/components/ErrorNoteStatsView";
import { SessionList } from "@/components/session-picker/SessionList";
import { getStudyProgressSnapshot } from "@/lib/server-actions/studyProgressSnapshotActions";
import { STUDY_PLAN_LABELS } from "@/lib/study-plan";
import type {
  StudyProgressSnapshot,
  StudyPlanCompletionSummary,
  PastExamCompletionSnapshot,
} from "@/types/study-progress-snapshot";

type DialogMode =
  | "stats"
  | "problemHistory"
  | "attendance"
  | "badges"
  | "errorNotes";

interface StudyProgressPayload {
  attendanceSummary: {
    theory: { attendedCount: number; totalPages: number };
    coding: { attendedCount: number; totalPages: number };
  };
  theory: Array<{
    subject: string;
    subjectDisplayName: string;
    attendedCount: number;
    totalPages: number;
    attendedPages?: Array<{ slug: string; attendedAt: string }>;
  }>;
  coding: Array<{
    subject: string;
    subjectDisplayName: string;
    attendedCount: number;
    totalPages: number;
    attendedPages?: Array<{ slug: string; attendedAt: string }>;
  }>;
  problems: {
    total: {
      totalCount: number;
      attempted: number;
      correct: number;
      incorrect: number;
      accuracyRate: number;
      attemptRate: number;
    };
    bySubject: Array<{
      subject: string;
      subjectDisplayName: string;
      totalCount: number;
      attempted: number;
      correct: number;
      incorrect: number;
      accuracyRate: number;
      attemptRate: number;
      scopes: Array<{
        scope: string;
        scopeDisplayName: string;
        totalCount: number;
        attempted: number;
        correct: number;
        incorrect: number;
        accuracyRate: number;
        attemptRate: number;
      }>;
    }>;
  };
  examSessionDisplayName?: string;
}

// StudyProgressPage와 동일한 분류 유지
const THEORY_SUBJECT_KEYS = ["DB", "network-os", "sw-dev", "sw-design", "security-newtech"];
const CODING_SUBJECT_KEYS = ["c", "java", "python"];
const CATEGORY_DISPLAY: Record<"theory" | "coding", string> = {
  theory: "이론",
  coding: "코딩",
};

type StatsVariant = "overview" | "problems";

interface CommonProps {
  trigger: React.ReactNode;
  // 활성 회차를 제외할지 여부 (기본 true — 활성 회차는 이미 기본 화면에 표시되므로)
  excludeActive?: boolean;
}

interface StatsProps extends CommonProps {
  mode: "stats";
  // "overview"는 전체 탭 레이아웃(전체 현황 카드), "problems"는 문제 탭 레이아웃(계층 필터)
  statsView?: StatsVariant;
}

interface ProblemHistoryProps extends CommonProps {
  mode: "problemHistory";
  userId: string;
  problemId: string;
}

interface AttendanceProps extends CommonProps {
  mode: "attendance";
  userId: string;
}

interface BadgesProps extends CommonProps {
  mode: "badges";
  userId: string;
}

interface ErrorNotesProps extends CommonProps {
  mode: "errorNotes";
  userId: string;
}

type PreviousSessionDialogProps =
  | StatsProps
  | ProblemHistoryProps
  | AttendanceProps
  | BadgesProps
  | ErrorNotesProps;

function formatTimestamp(value: number | string): string {
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  return date.toLocaleString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatsView({
  sessionId,
  variant,
}: {
  sessionId: string;
  variant: StatsVariant;
}) {
  const [snapshot, setSnapshot] = useState<StudyProgressSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // 회차별 불변 스냅샷을 읽는다(없으면 서버에서 read-or-create). JCG-3284.
        const result = await getStudyProgressSnapshot(sessionId);
        if (cancelled) return;
        if (!result.success || !result.snapshot) {
          setError(result.error || "데이터를 불러올 수 없습니다.");
          setSnapshot(null);
        } else {
          setSnapshot(result.snapshot);
        }
      } catch (err) {
        console.error("이전 회차 통계 조회 실패:", err);
        if (!cancelled) setError("데이터를 불러올 수 없습니다.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">
        {error ?? "데이터 없음"}
      </div>
    );
  }

  if (variant === "overview") {
    return (
      <OverviewStatsPanel
        data={snapshot.studyProgress}
        studyPlanCompletion={snapshot.studyPlanCompletion}
        pastExamCompletion={snapshot.pastExamCompletion}
      />
    );
  }
  return <ProblemsStatsPanel data={snapshot.studyProgress} />;
}

function OverviewStatsPanel({
  data,
  studyPlanCompletion,
  pastExamCompletion,
}: {
  data: StudyProgressPayload;
  studyPlanCompletion: StudyPlanCompletionSummary[];
  pastExamCompletion: PastExamCompletionSnapshot;
}) {
  const { theory, coding } = data.attendanceSummary;
  const total = data.problems.total;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4" />
            전체 현황
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">이론 출석</span>
              <span className="text-sm font-medium">
                {theory.attendedCount} / {theory.totalPages} 페이지
              </span>
            </div>
            <Progress
              value={
                theory.totalPages > 0
                  ? (theory.attendedCount / theory.totalPages) * 100
                  : 0
              }
              className="h-2"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">코딩 출석</span>
              <span className="text-sm font-medium">
                {coding.attendedCount} / {coding.totalPages} 페이지
              </span>
            </div>
            <Progress
              value={
                coding.totalPages > 0
                  ? (coding.attendedCount / coding.totalPages) * 100
                  : 0
              }
              className="h-2"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">문제 시도</span>
              <span className="text-sm font-medium">
                {total.attempted} / {total.totalCount}문제
                <span className="text-gray-400 ml-1">
                  ({total.attemptRate.toFixed(0)}%)
                </span>
              </span>
            </div>
            <Progress value={total.attemptRate} className="h-2" />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">시도 중 정답률</span>
              <span
                className={`text-sm font-medium ${
                  total.accuracyRate >= 70
                    ? "text-green-600"
                    : total.accuracyRate >= 50
                    ? "text-amber-600"
                    : "text-red-600"
                }`}
              >
                {total.attempted > 0 ? total.accuracyRate.toFixed(1) : 0}%
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-2 text-center text-xs">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
              <p className="text-blue-600 dark:text-blue-400">시도</p>
              <p className="font-semibold text-lg text-blue-600 dark:text-blue-400">
                {total.attempted}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
              <p className="text-green-600 dark:text-green-400">정답</p>
              <p className="font-semibold text-lg text-green-600 dark:text-green-400">
                {total.correct}
              </p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
              <p className="text-red-600 dark:text-red-400">오답</p>
              <p className="font-semibold text-lg text-red-600 dark:text-red-400">
                {total.incorrect}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 학습 계획 완주 (세션 마감 요약, read-only) — 구독 계획이 있을 때만 */}
      {studyPlanCompletion.length > 0 && (
        <StudyPlanCompletionCard plans={studyPlanCompletion} />
      )}

      {/* 기출 완주 현황 (요약) — 활성 페이지 StudyProgressPage와 동일한 공용 카드(summary 변형).
          detailed 미지정 → 진행바 요약만(박스 그리드·네비게이션 없음)이라 read-only 다이얼로그에 적합. JCG-3284 */}
      {pastExamCompletion.totalRounds > 0 && (
        <PastExamCompletionCard
          completedRoundCount={pastExamCompletion.completedRoundCount}
          totalRoundCount={pastExamCompletion.totalRounds}
          completionByRound={pastExamCompletion.counts}
          allRounds={pastExamCompletion.allRounds}
        />
      )}
    </div>
  );
}

/**
 * 학습 계획 완주 요약(다이얼로그·read-only, JCG-3284).
 *
 * 활성 화면의 StudyPlanOnTrackCard(amber·CalendarRange 도메인 톤)와 시각 정체성을 맞춘다.
 * 단 오늘 마커·D-day·페이스 같은 시간의존 표현은 넣지 않고, 구독 계획별 완주 진행바
 * (doneTotal/trackableTasks)와 완주/미완주 라벨만 보여준다. 진행바는 학습계획 도메인 색인
 * amber→orange 그라데이션으로, 기출 완주 카드의 violet→indigo와 카드별로 구분된다.
 */
function StudyPlanCompletionCard({
  plans,
}: {
  plans: StudyPlanCompletionSummary[];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarRange
            className="h-4 w-4 text-amber-500 dark:text-amber-400"
            aria-hidden="true"
          />
          학습 계획 완주
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {plans.map((plan) => {
          const pct =
            plan.trackableTasks > 0
              ? (plan.doneTotal / plan.trackableTasks) * 100
              : 0;
          const remaining = Math.max(0, plan.trackableTasks - plan.doneTotal);
          const label = STUDY_PLAN_LABELS[plan.planId];
          return (
            <div key={plan.planId}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {label}
                </span>
                {plan.completed ? (
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-600 dark:text-amber-300">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    완주
                  </span>
                ) : (
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    미완주 · {remaining}개 남음
                  </span>
                )}
              </div>
              <Progress
                value={pct}
                aria-label={`${label} 완주 진행률 ${Math.round(pct)}%`}
                className="h-2 [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-amber-500 [&>[data-slot=progress-indicator]]:to-orange-500 dark:[&>[data-slot=progress-indicator]]:from-amber-400 dark:[&>[data-slot=progress-indicator]]:to-orange-400"
              />
              <div className="mt-1 text-right text-xs text-gray-400 tabular-nums">
                {plan.doneTotal} / {plan.trackableTasks}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ProblemsStatsPanel({ data }: { data: StudyProgressPayload }) {
  const [breadcrumb, setBreadcrumb] = useState<{
    category: "all" | "theory" | "coding";
    subject: string | null;
  }>({ category: "all", subject: null });

  const total = data.problems.total;

  const getSubjectsForCategory = (category: "theory" | "coding") => {
    const keys = category === "theory" ? THEORY_SUBJECT_KEYS : CODING_SUBJECT_KEYS;
    return data.problems.bySubject.filter((s) => keys.includes(s.subject));
  };

  const getCategoryStats = (category: "theory" | "coding") => {
    const subjects = getSubjectsForCategory(category);
    const agg = subjects.reduce(
      (acc, s) => ({
        totalCount: acc.totalCount + s.totalCount,
        attempted: acc.attempted + s.attempted,
        correct: acc.correct + s.correct,
        incorrect: acc.incorrect + s.incorrect,
      }),
      { totalCount: 0, attempted: 0, correct: 0, incorrect: 0 }
    );
    return {
      ...agg,
      attemptRate: agg.totalCount > 0 ? (agg.attempted / agg.totalCount) * 100 : 0,
      accuracyRate: agg.attempted > 0 ? (agg.correct / agg.attempted) * 100 : 0,
    };
  };

  const accClassLarge = (rate: number) =>
    rate >= 70
      ? "font-semibold text-green-600"
      : rate >= 50
      ? "font-semibold text-amber-600"
      : "font-semibold text-red-600";
  const accClass = (rate: number) =>
    rate >= 70 ? "text-green-600" : rate >= 50 ? "text-amber-600" : "text-red-600";

  return (
    <div className="space-y-4">
      {/* 계층 필터 */}
      <div className="space-y-2">
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setBreadcrumb({ category: "all", subject: null })}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              breadcrumb.category === "all"
                ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
            }`}
          >
            전체
          </button>
          {(["theory", "coding"] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => setBreadcrumb({ category: cat, subject: null })}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                breadcrumb.category === cat
                  ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              }`}
            >
              {CATEGORY_DISPLAY[cat]}
            </button>
          ))}
        </div>

        {breadcrumb.category !== "all" && (
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() =>
                setBreadcrumb({ category: breadcrumb.category, subject: null })
              }
              className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                !breadcrumb.subject
                  ? "bg-blue-600 text-white"
                  : "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
              }`}
            >
              전체
            </button>
            {getSubjectsForCategory(breadcrumb.category).map((subj) => (
              <button
                key={subj.subject}
                onClick={() =>
                  setBreadcrumb({ category: breadcrumb.category, subject: subj.subject })
                }
                className={`px-2.5 py-0.5 rounded-full text-xs transition-colors ${
                  breadcrumb.subject === subj.subject
                    ? "bg-blue-600 text-white"
                    : "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                }`}
              >
                {subj.subjectDisplayName}
              </button>
            ))}
          </div>
        )}
      </div>

      {breadcrumb.category === "all" && (
        <Card>
          <CardContent className="py-4 space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="font-semibold text-sm">전체</span>
                <span className="text-xs text-gray-500">
                  정답률{" "}
                  <span className={accClassLarge(total.accuracyRate)}>
                    {total.attempted > 0 ? total.accuracyRate.toFixed(0) : 0}%
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Progress value={total.attemptRate} className="h-2" />
                </div>
                <span className="text-xs font-medium text-blue-600 w-14 text-right">
                  {total.attempted}/{total.totalCount}
                </span>
              </div>
            </div>

            <div className="border-t dark:border-gray-700" />

            {(["theory", "coding"] as const).map((cat) => {
              const catStats = getCategoryStats(cat);
              const subjects = getSubjectsForCategory(cat);
              return (
                <div key={cat}>
                  <button
                    onClick={() => setBreadcrumb({ category: cat, subject: null })}
                    className="w-full text-left group"
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-sm group-hover:text-blue-600 transition-colors">
                        {CATEGORY_DISPLAY[cat]}
                      </span>
                      <span className="text-xs text-gray-500">
                        정답률{" "}
                        <span className={accClassLarge(catStats.accuracyRate)}>
                          {catStats.attempted > 0 ? catStats.accuracyRate.toFixed(0) : 0}%
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Progress value={catStats.attemptRate} className="h-1.5" />
                      </div>
                      <span className="text-xs font-medium text-blue-600 w-14 text-right">
                        {catStats.attempted}/{catStats.totalCount}
                      </span>
                    </div>
                  </button>

                  <div className="mt-2 ml-3 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-2">
                    {subjects.map((subj) => (
                      <button
                        key={subj.subject}
                        onClick={() => setBreadcrumb({ category: cat, subject: subj.subject })}
                        className="w-full text-left group"
                      >
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-blue-600 transition-colors">
                            {subj.subjectDisplayName}
                          </span>
                          <span className="text-xs text-gray-400">
                            <span className={accClass(subj.accuracyRate)}>
                              {subj.attempted > 0 ? subj.accuracyRate.toFixed(0) : 0}%
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Progress value={subj.attemptRate} className="h-1" />
                          </div>
                          <span className="text-xs text-gray-500 w-14 text-right">
                            {subj.attempted}/{subj.totalCount}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {breadcrumb.category !== "all" &&
        !breadcrumb.subject &&
        (() => {
          const subjects = getSubjectsForCategory(breadcrumb.category);
          const catStats = getCategoryStats(breadcrumb.category);
          return (
            <>
              <Card>
                <CardContent className="py-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">
                      {CATEGORY_DISPLAY[breadcrumb.category]}
                    </span>
                    <span className="font-semibold text-blue-600">
                      {catStats.attempted} / {catStats.totalCount}
                    </span>
                  </div>
                  <Progress value={catStats.attemptRate} className="h-2 mb-2" />
                  <div className="flex gap-4 text-xs text-gray-500 flex-wrap">
                    <span className="text-blue-600">시도 {catStats.attempted}</span>
                    <span className="text-green-600">정답 {catStats.correct}</span>
                    <span className="text-red-600">오답 {catStats.incorrect}</span>
                    <span className={accClass(catStats.accuracyRate)}>
                      정답률{" "}
                      {catStats.attempted > 0 ? catStats.accuracyRate.toFixed(0) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>

              {subjects.map((subject) => (
                <Card key={subject.subject}>
                  <CardContent className="py-4">
                    <div className="flex justify-between items-center mb-2">
                      <button
                        onClick={() =>
                          setBreadcrumb({ ...breadcrumb, subject: subject.subject })
                        }
                        className="font-medium hover:text-blue-600 transition-colors"
                      >
                        {subject.subjectDisplayName}
                      </button>
                      <span className="font-semibold text-blue-600">
                        {subject.attempted} / {subject.totalCount}
                      </span>
                    </div>
                    <Progress value={subject.attemptRate} className="h-2 mb-2" />
                    <div className="flex gap-4 text-xs text-gray-500 mb-3 flex-wrap">
                      <span className="text-blue-600">시도 {subject.attempted}</span>
                      <span className="text-green-600">정답 {subject.correct}</span>
                      <span className="text-red-600">오답 {subject.incorrect}</span>
                      <span className={accClass(subject.accuracyRate)}>
                        정답률{" "}
                        {subject.attempted > 0 ? subject.accuracyRate.toFixed(0) : 0}%
                      </span>
                    </div>
                    {subject.scopes.length > 0 && (
                      <div className="border-t pt-2 space-y-2">
                        {subject.scopes.map((scope) => (
                          <div key={scope.scope} className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-24 truncate">
                              {scope.scopeDisplayName}
                            </span>
                            <div className="flex-1">
                              <Progress value={scope.attemptRate} className="h-1.5" />
                            </div>
                            <span className="text-xs font-medium w-16 text-right text-blue-600">
                              {scope.attempted}/{scope.totalCount}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </>
          );
        })()}

      {breadcrumb.subject &&
        (() => {
          const subject = data.problems.bySubject.find(
            (s) => s.subject === breadcrumb.subject
          );
          if (!subject) return null;
          return (
            <Card>
              <CardContent className="py-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{subject.subjectDisplayName}</span>
                  <span className="font-semibold text-blue-600">
                    {subject.attempted} / {subject.totalCount}
                  </span>
                </div>
                <Progress value={subject.attemptRate} className="h-2 mb-2" />
                <div className="flex gap-4 text-xs text-gray-500 mb-3 flex-wrap">
                  <span className="text-blue-600">시도 {subject.attempted}</span>
                  <span className="text-green-600">정답 {subject.correct}</span>
                  <span className="text-red-600">오답 {subject.incorrect}</span>
                  <span className={accClass(subject.accuracyRate)}>
                    정답률{" "}
                    {subject.attempted > 0 ? subject.accuracyRate.toFixed(0) : 0}%
                  </span>
                </div>
                {subject.scopes.length > 0 && (
                  <div className="border-t pt-2 space-y-2">
                    {subject.scopes.map((scope) => (
                      <div key={scope.scope} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-24 truncate">
                          {scope.scopeDisplayName}
                        </span>
                        <div className="flex-1">
                          <Progress value={scope.attemptRate} className="h-1.5" />
                        </div>
                        <span className="text-xs font-medium w-16 text-right text-blue-600">
                          {scope.attempted}/{scope.totalCount}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}
    </div>
  );
}

function ProblemHistoryView({
  userId,
  problemId,
  sessionId,
}: {
  userId: string;
  problemId: string;
  sessionId: string;
}) {
  const [attempts, setAttempts] = useState<ProblemAttempt[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      const result = await getProblemHistoryForSession(userId, problemId, sessionId);
      if (cancelled) return;
      if (!result.success) {
        setError(result.error ?? "데이터를 불러올 수 없습니다.");
        setAttempts(null);
      } else {
        setAttempts(result.history?.attempts ?? []);
      }
      setIsLoading(false);
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [userId, problemId, sessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">{error}</div>
    );
  }

  if (!attempts || attempts.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        해당 회차에 풀이 기록이 없습니다.
      </div>
    );
  }

  const correctCount = attempts.filter((a) => a.isCorrect).length;
  const incorrectCount = attempts.length - correctCount;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 text-xs text-gray-600 dark:text-gray-400">
        <span className="inline-flex items-center gap-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
          정답 {correctCount}
        </span>
        <span className="inline-flex items-center gap-1">
          <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
          오답 {incorrectCount}
        </span>
        <span className="ml-auto">총 {attempts.length}회</span>
      </div>
      <ul className="divide-y divide-gray-100 dark:divide-neutral-700 rounded-md border border-gray-200 dark:border-neutral-700">
        {attempts
          .slice()
          .reverse()
          .map((attempt, index) => (
            <li
              key={`${attempt.timestamp}-${index}`}
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2">
                {attempt.isCorrect ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                )}
                <span className="text-gray-700 dark:text-gray-300">
                  {attempt.isCorrect ? "정답" : "오답"}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatTimestamp(attempt.timestamp)}
              </span>
            </li>
          ))}
      </ul>
    </div>
  );
}

function AttendanceView({ sessionId }: { sessionId: string }) {
  const [data, setData] = useState<StudyProgressPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [innerTab, setInnerTab] = useState<"theory" | "coding">("theory");

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `/api/study-progress?sessionId=${encodeURIComponent(sessionId)}`,
          { cache: "no-store" }
        );
        const payload = await response.json();
        if (cancelled) return;
        if (!payload.success) {
          setError(payload.error || "데이터를 불러올 수 없습니다.");
          setData(null);
        } else {
          setData(payload as StudyProgressPayload);
        }
      } catch (err) {
        console.error("이전 회차 출석 조회 실패:", err);
        if (!cancelled) setError("데이터를 불러올 수 없습니다.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">
        {error ?? "데이터 없음"}
      </div>
    );
  }

  const subjects = innerTab === "theory" ? data.theory : data.coding;
  const allEmpty = subjects.every((s) => s.attendedCount === 0);

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <Button
          variant={innerTab === "theory" ? "default" : "ghost"}
          size="sm"
          onClick={() => setInnerTab("theory")}
          className="rounded-b-none"
        >
          이론
        </Button>
        <Button
          variant={innerTab === "coding" ? "default" : "ghost"}
          size="sm"
          onClick={() => setInnerTab("coding")}
          className="rounded-b-none"
        >
          코딩
        </Button>
      </div>

      <div className="space-y-4">
        {subjects.map((subject) => (
          <Card key={subject.subject}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <span className="font-medium">{subject.subjectDisplayName}</span>
                <Badge
                  variant={subject.attendedCount > 0 ? "default" : "secondary"}
                >
                  {subject.attendedCount} / {subject.totalPages} 페이지
                </Badge>
              </div>
              {subject.attendedPages && subject.attendedPages.length > 0 && (
                <div className="mt-2 space-y-1">
                  {subject.attendedPages.slice(0, 3).map((page) => (
                    <div
                      key={page.slug}
                      className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"
                    >
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span className="truncate">{page.slug}</span>
                    </div>
                  ))}
                  {subject.attendedPages.length > 3 && (
                    <p className="text-xs text-gray-400">
                      +{subject.attendedPages.length - 3}개 더
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {allEmpty && (
          <div className="text-center py-8 text-gray-500">
            <BookOpen className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>해당 회차에 출석 기록이 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorNotesView({
  userId,
  sessionId,
}: {
  userId: string;
  sessionId: string;
}) {
  const [histories, setHistories] = useState<ProblemHistory[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      const result = await getAllProblemHistoriesForSession(userId, sessionId);
      if (cancelled) return;
      if (!result.success) {
        setError(result.error ?? "데이터를 불러올 수 없습니다.");
        setHistories(null);
      } else {
        setHistories(result.histories);
      }
      setIsLoading(false);
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [userId, sessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">
        {error}
      </div>
    );
  }

  return <ErrorNoteStatsView histories={histories ?? []} />;
}

function BadgesView({
  userId,
  sessionId,
}: {
  userId: string;
  sessionId: string;
}) {
  const [badges, setBadges] = useState<UserBadge[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      const result = await getUserBadgesForSession(userId, sessionId);
      if (cancelled) return;
      setBadges(result);
      setIsLoading(false);
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [userId, sessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!badges || badges.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        해당 회차에 획득한 도전과제가 없습니다.
      </div>
    );
  }

  const sorted = badges.slice().sort((a, b) => b.earnedAt - a.earnedAt);

  return (
    <div className="space-y-2">
      <div className="text-xs text-gray-600 dark:text-gray-400">
        획득 {sorted.length}개
      </div>
      <ul className="space-y-2">
        {sorted.map((badge) => {
          const def = BADGES[badge.badgeId];
          if (!def) return null;
          const rarityStyle = RARITY_STYLES[def.rarity];
          return (
            <li
              key={badge.badgeId}
              className={`flex items-center gap-3 rounded-lg border p-2 ${rarityStyle.bg} ${rarityStyle.border}`}
            >
              <StaticBadgeMedal badgeId={badge.badgeId} size="sm" boxClassName="w-12 h-12" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{def.name}</div>
                <div className="truncate text-xs text-gray-600 dark:text-gray-400">
                  {def.description}
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(badge.earnedAt).toLocaleDateString("ko-KR")}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function PreviousSessionDialog(
  props: PreviousSessionDialogProps
) {
  const { trigger, excludeActive = true, mode } = props;
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<ExamSession[] | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState<ExamSession | null>(null);

  const loadSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const result = await listExamSessions();
      if (result.success) {
        const filtered = excludeActive
          ? result.sessions.filter((s) => !s.isActive)
          : result.sessions;
        setSessions(filtered);
      } else {
        setSessions([]);
      }
    } finally {
      setIsLoadingSessions(false);
    }
  }, [excludeActive]);

  // 다이얼로그 열림 시 세션 fetch — JCG-2034 마이그레이션 예정
  useEffect(() => {
    if (open && sessions === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadSessions();
    }
  }, [open, sessions, loadSessions]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSelectedSession(null);
    }
  };

  const titleByMode: Record<DialogMode, string> = {
    stats: "이전 회차 통계",
    problemHistory: "이전 회차 풀이 기록",
    attendance: "이전 회차 출석",
    badges: "이전 회차 도전과제",
    errorNotes: "이전 회차 오답노트 통계",
  };

  const descriptionByMode: Record<DialogMode, string> = {
    stats: "과거 시험 회차의 학습 통계를 확인합니다. 조회만 가능합니다.",
    problemHistory: "이 문제의 과거 풀이 기록을 확인합니다.",
    attendance: "과거 시험 회차의 출석 기록을 확인합니다.",
    badges: "과거 시험 회차에 획득한 도전과제를 확인합니다.",
    errorNotes: "과거 시험 회차의 오답노트 통계를 확인합니다.",
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`${
          mode === "errorNotes"
            ? "sm:max-w-5xl"
            : mode === "stats"
            ? "sm:max-w-2xl"
            : "sm:max-w-md"
        } max-h-[85vh] overflow-hidden flex flex-col z-[99999]`}
        overlayClassName="z-[99998]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-4 w-4" />
            {selectedSession
              ? `${selectedSession.displayName} — ${titleByMode[mode]}`
              : titleByMode[mode]}
          </DialogTitle>
          <DialogDescription>{descriptionByMode[mode]}</DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-1">
          {selectedSession === null ? (
            isLoadingSessions || sessions === null ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <SessionList sessions={sessions} onSelect={setSelectedSession} />
            )
          ) : (
            <div className="space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedSession(null)}
                className="self-start px-2 text-xs"
              >
                ← 회차 다시 선택
              </Button>
              {mode === "stats" && (
                <StatsView
                  sessionId={selectedSession.id}
                  variant={props.statsView ?? "overview"}
                />
              )}
              {mode === "problemHistory" && (
                <ProblemHistoryView
                  userId={props.userId}
                  problemId={props.problemId}
                  sessionId={selectedSession.id}
                />
              )}
              {mode === "attendance" && (
                <AttendanceView sessionId={selectedSession.id} />
              )}
              {mode === "badges" && (
                <BadgesView
                  userId={props.userId}
                  sessionId={selectedSession.id}
                />
              )}
              {mode === "errorNotes" && (
                <ErrorNotesView
                  userId={props.userId}
                  sessionId={selectedSession.id}
                />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
